import React, { ReactElement, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import { initialForgotPasswordFormValues } from 'models/form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from 'pages/ResetPasswordPage/schemaValidation';
import { EButtonVariants } from 'constants/Buttons';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import { usePassword } from 'models/passwordContext';
import { Alert } from 'components/atoms/Alert';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { useNavigate } from 'react-router-dom';
import { AuthTemplate, StyledAuthHeader } from 'components/templates/AuthTemplate';
import { IChangePasswordBodyRequest } from 'models/password';
import { useQuery } from 'hooks/useQuery';
import { FC_LICENSING_DEVICE_ID } from 'pages/LoginPage';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageSubTitle, StyledPageTitle } from 'theme/styles';

const ResetPasswordPage = (): ReactElement => {
  const { t } = useTranslations();
  const [isResetPasswordSuccessfully, setIsResetPasswordSuccessfully] = useState<
    boolean | null
  >(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoaded, setLoaded] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [alertText, setAlertText] = useState<string | null>(null);
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: initialForgotPasswordFormValues,
    ...(isResetPasswordSuccessfully === null ? { resolver: yupResolver(schema) as any } : {}),
  });
  const { changePassword, isForgotPasswordLoading } = usePassword();

  const query = useQuery();
  const accountUuid: any = query.get('accountUuid');
  const verificationCode: any = query.get('verificationCode');
  const queryParams: any = new URLSearchParams({
    accountUuid,
    verificationCode,
  });

  // const encodedEmail: any = query.get('e');
  // let decodedEmail = '';

  // try {
  //   decodedEmail = encodedEmail ? atob(encodedEmail?.toString()) : '';
  // } catch (e: any) {
  //   navigate(ERouteLinks.Auth);
  // }

  // useEffect(() => {
  //   (async function callValidateResetPasswordLink() {
  //     try {
  //       await validateResetPasswordLink(token);
  //     } catch (e: any) {
  //       setIsResetPasswordSuccessfully(false);
  //       if (e.response.status === 410) {
  //         setAlertText(
  //           'The reset password link has expired. To reset your password, click on the button below.',
  //         );
  //       } else if (e.response.status === 400) {
  //         setAlertText('The reset password link has been already used.');
  //       }
  //     } finally {
  //       setLoaded(true);
  //     }
  //   })();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  const handleChangePasswordSubmit = handleSubmit(
    (({ newPassword }: IChangePasswordBodyRequest) => {
      changePassword(
        { newPassword },
        queryParams,
        (res) => {
          localStorage.removeItem(FC_LICENSING_DEVICE_ID);
          if (res.accessToken) {
            setIsResetPasswordSuccessfully(true);
          } else {
            navigate(ERouteLinks.Auth);
          }
        },
        () => {
          setIsResetPasswordSuccessfully(false);
        },
      );
    }) as any,
  );

  const renderResetPasswordTemplate = () => {
    if (isLoaded) {
      if (isResetPasswordSuccessfully == null) {
        return (
          <Grid item container xs={12} gap={3}>
            <Grid item xs={12} gap={2}>
              <Grid item xs={12}>
                <FormInput
                  name="newPassword"
                  type={EFormInputTypes.password}
                  label={t('page.newPassword.input.newPassword')}
                  placeholder={t('page.newPassword.input.newPassword')}
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormInput
                  name="retypeNewPassword"
                  type={EFormInputTypes.password}
                  label={t('page.newPassword.input.retypeNewPassword')}
                  placeholder={t('page.newPassword.input.retypeNewPassword')}
                  control={control}
                  errors={errors}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: { xs: 32, sm: 40 },
                    },
                  }}
                />
              </Grid>
            </Grid>

            <Grid item container xs={12} gap={3}>
              <Grid item container xs={12} gap={1.3} justifyItems="center">
                <Grid item xs={12}>
                  <Button
                    label={t('page.newPassword.button.confirm')}
                    onClick={handleChangePasswordSubmit}
                    isLoading={isForgotPasswordLoading}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    id="start-page"
                    label={t('page.newPassword.button.cancel')}
                    variant={EButtonVariants.outlined}
                    isLoading={isForgotPasswordLoading}
                    fullWidth
                    onClick={() => navigate(`${ERouteLinks.Auth}`)}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        );
      }
      if (isResetPasswordSuccessfully) {
        return (
          <>
            <Grid item xs={12} mt={12} mb={8}>
              <StyledAuthHeader mb={4}>Password Reset</StyledAuthHeader>
            </Grid>
            <Grid item mb={10}>
              <Alert text="Your account has been activated successfully. Click Continue button to go to the application." />
            </Grid>
            <Grid item xs={12} sm={7} xl={12} container>
              <Button
                id="start-page"
                label={t('page.newPassword.button.login')}
                variant={EButtonVariants.contained}
                isLoading={isForgotPasswordLoading}
                fullWidth
                onClick={() => navigate(`${ERouteLinks.Auth}`)}
              />
            </Grid>
          </>
        );
      }
    }
    return null;
  };

  if (isLoaded) {
    return (
      <AuthTemplate>
        <Grid item container xs={12} gap={7}>
          <Grid item container xs={12} gap={1}>
            <Grid item xs={12}>
              <StyledPageTitle variant="h4">{t('page.newPassword.title')}</StyledPageTitle>
            </Grid>
            <Grid item xs={12}>
              <StyledPageSubTitle variant="body2">
                {t('page.newPassword.subtitle')}
                <li>
                  <Typography variant="caption">
                    {t('page.newPassword.subtitle.point1')}
                  </Typography>
                </li>
                <li>
                  <Typography variant="caption">
                    {t('page.newPassword.subtitle.point2')}
                  </Typography>
                </li>
              </StyledPageSubTitle>
            </Grid>
          </Grid>
          {renderResetPasswordTemplate()}
        </Grid>
      </AuthTemplate>
    );
  }
  return <div />;
};
export { ResetPasswordPage };
